import type { NpcRiderConfig } from "../../config/types";
import { headingPose } from "../riderPose";
import type { Agent } from "./types";

export interface Blocker {
    x: number;
    y: number;
    halfWidth: number;
}

export interface NpcRider extends Agent {
    heading: number;
    fallen: number;
}

const LANES = [-4, -3, -2, -1, 0, 1, 2, 3, 4];
const TRAIL_PULL = 0.3;
const MAX_HEADING = 50;
const RAD = Math.PI / 180;

export function createNpcRider(x: number, y: number): NpcRider {
    return { x, y, pose: "down", flipX: false, heading: 0, fallen: 0 };
}

export function crashNpcRider(npc: NpcRider, cfg: NpcRiderConfig): NpcRider {
    return { ...npc, fallen: cfg.fallenS, pose: "fallen" };
}

const reachOf = (cfg: NpcRiderConfig) => cfg.lookAheadM / 3;

function pathIsClear(npc: NpcRider, laneX: number, blockers: Blocker[], cfg: NpcRiderConfig): boolean {
    return blockers.every((b) => {
        if (b.y <= npc.y || b.y >= npc.y + cfg.lookAheadM) return true;
        const along = Math.min(1, (b.y - npc.y) / reachOf(cfg));
        const pathX = npc.x + (laneX - npc.x) * along;
        return Math.abs(b.x - pathX) >= b.halfWidth + cfg.clearanceM;
    });
}

export function chooseLane(npc: NpcRider, trailX: number, blockers: Blocker[], cfg: NpcRiderConfig): number {
    const cost = (laneX: number) => Math.abs(laneX - npc.x) + TRAIL_PULL * Math.abs(laneX - trailX);
    const clear = LANES.map((lane) => trailX + lane * cfg.laneM)
        .filter((laneX) => pathIsClear(npc, laneX, blockers, cfg))
        .sort((a, b) => cost(a) - cost(b));
    return clear[0] ?? npc.x;
}

export function stepNpcRider(npc: NpcRider, trailX: number, blockers: Blocker[], dt: number, cfg: NpcRiderConfig): NpcRider {
    if (npc.fallen > 0) return { ...npc, fallen: Math.max(0, npc.fallen - dt), pose: "fallen" };

    const targetX = chooseLane(npc, trailX, blockers, cfg);
    const wanted = Math.max(-MAX_HEADING, Math.min(MAX_HEADING, Math.atan2(targetX - npc.x, reachOf(cfg)) / RAD));
    const maxTurn = cfg.turnRate * dt;
    const heading = npc.heading + Math.max(-maxTurn, Math.min(maxTurn, wanted - npc.heading));

    return {
        ...npc,
        heading,
        x: npc.x + cfg.speed * Math.sin(heading * RAD) * dt,
        y: npc.y + cfg.speed * Math.cos(heading * RAD) * dt,
        pose: headingPose(heading),
        flipX: heading >= 15,
    };
}
